import { useState, useEffect } from "react";
import {
  Receipt, CreditCard, Download, Search, Filter, Eye, Send,
  FileText, TrendingUp, Clock, CheckCircle, XCircle, RefreshCw,
  Calendar, User, Building2, ChevronDown, MoreHorizontal, AlertCircle,
  FileSpreadsheet, FileDown, Euro, Bell, Repeat, Archive, Plus, Trash2,
  Play, Pause, Mail
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_STATUS_CONFIG = {
  pending: { label: "Offen", color: "bg-yellow-500/20 text-yellow-500", icon: Clock },
  paid: { label: "Bezahlt", color: "bg-green-500/20 text-green-500", icon: CheckCircle },
  partial: { label: "Teilweise", color: "bg-blue-500/20 text-blue-500", icon: AlertCircle },
  refunded: { label: "Erstattet", color: "bg-purple-500/20 text-purple-500", icon: RefreshCw },
  cancelled: { label: "Storniert", color: "bg-red-500/20 text-red-500", icon: XCircle },
  corrected: { label: "Korrigiert", color: "bg-gray-500/20 text-gray-500", icon: RefreshCw },
  not_payable: { label: "Nicht zahlbar", color: "bg-gray-500/20 text-gray-400", icon: XCircle },
};

const CATEGORY_CONFIG = {
  personal_training: { label: "Personal Training", color: "bg-[#c1ff72]/20 text-[#c1ff72]" },
  package: { label: "Paket", color: "bg-blue-500/20 text-blue-500" },
  camp: { label: "Camp", color: "bg-orange-500/20 text-orange-500" },
  academy: { label: "Akademie", color: "bg-purple-500/20 text-purple-500" },
  other: { label: "Sonstiges", color: "bg-gray-500/20 text-gray-500" },
};

const PAYMENT_METHOD_LABELS = {
  paypal: "PayPal",
  bank_transfer: "Banküberweisung",
  cash: "Barzahlung",
  credit: "Guthaben",
};

export default function BuchhaltungTab({ adminToken }) {
  const [activeSubTab, setActiveSubTab] = useState("invoices"); // invoices | recurring
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  
  // Recurring invoices state
  const [recurringInvoices, setRecurringInvoices] = useState([]);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [newRecurring, setNewRecurring] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    service_category: "personal_training",
    description: "",
    amount: "",
    payment_method: "bank_transfer",
    interval_months: 1,
    next_invoice_date: "",
    notes: ""
  });
  
  // Reminder state
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderMessage, setReminderMessage] = useState("");
  
  // Price correction state
  const [priceCorrections, setPriceCorrections] = useState(null);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [correctionInProgress, setCorrectionInProgress] = useState(false);
  const [correctionResult, setCorrectionResult] = useState(null);
  
  // Akademie Massen-Korrektur state
  const [akademieCorrections, setAkademieCorrections] = useState(null);
  const [akademieCorrectionLoading, setAkademieCorrectionLoading] = useState(false);
  const [akademieCorrectionResult, setAkademieCorrectionResult] = useState(null);
  
  // Test-PDF state
  const [testPdfLoading, setTestPdfLoading] = useState(null); // invoice_id of loading PDF
  const [testPdfResult, setTestPdfResult] = useState(null);
  
  // Audit state
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => {
    fetchInvoices();
    fetchStats();
    fetchRecurringInvoices();
    fetchPriceCorrections();
  }, [adminToken]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/invoices/admin/all?`;
      if (statusFilter !== "all") url += `payment_status=${statusFilter}&`;
      if (categoryFilter !== "all") url += `category=${categoryFilter}&`;
      if (dateFrom) url += `date_from=${dateFrom}&`;
      if (dateTo) url += `date_to=${dateTo}&`;
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}&`;

      const res = await fetch(url, {
        headers: { "X-Admin-Token": adminToken }
      });
      const data = await res.json();
      setInvoices(data.invoices || []);
    } catch (err) {
      toast.error("Fehler beim Laden der Rechnungen");
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/admin/stats`, {
        headers: { "X-Admin-Token": adminToken }
      });
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  };

  const updatePaymentStatus = async (invoiceId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/admin/${invoiceId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Token": adminToken
        },
        body: JSON.stringify({ payment_status: newStatus })
      });

      if (res.ok) {
        toast.success("Zahlungsstatus aktualisiert");
        fetchInvoices();
        fetchStats();
        setShowDetailModal(false);
      } else {
        toast.error("Fehler beim Aktualisieren");
      }
    } catch (err) {
      toast.error("Ein Fehler ist aufgetreten");
    }
  };

  const cancelInvoice = async () => {
    if (!cancelReason.trim()) {
      toast.error("Bitte Stornierungsgrund angeben");
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/invoices/admin/${selectedInvoice.invoice_id}/cancel`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Token": adminToken
        },
        body: JSON.stringify({
          reason: cancelReason,
          refund_amount: refundAmount || null
        })
      });

      if (res.ok) {
        toast.success("Rechnung storniert");
        fetchInvoices();
        fetchStats();
        setShowCancelModal(false);
        setShowDetailModal(false);
        setCancelReason("");
        setRefundAmount("");
      } else {
        toast.error("Fehler beim Stornieren");
      }
    } catch (err) {
      toast.error("Ein Fehler ist aufgetreten");
    }
  };

  const resendInvoice = async (invoiceId) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/admin/${invoiceId}/resend`, {
        method: "POST",
        headers: { "X-Admin-Token": adminToken }
      });

      const data = await res.json();
      if (data.success) {
        toast.success(`Rechnung an ${data.email || 'Kunde'} versendet`);
      } else {
        toast.error(data.detail || data.message || "E-Mail konnte nicht gesendet werden");
      }
    } catch (err) {
      toast.error("Verbindungsfehler - bitte erneut versuchen");
    }
  };

  // Akademie Massen-Korrektur Funktionen
  const fetchAkademieCorrections = async () => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/admin/akademie/all-corrections-preview`, {
        headers: { "X-Admin-Token": adminToken }
      });
      const data = await res.json();
      setAkademieCorrections(data);
    } catch (err) {
      console.error("Failed to fetch akademie corrections:", err);
    }
  };

  const executeAkademieCorrections = async () => {
    setAkademieCorrectionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/invoices/admin/akademie/correct-all-and-send`, {
        method: "POST",
        headers: { "X-Admin-Token": adminToken }
      });
      const data = await res.json();
      setAkademieCorrectionResult(data);
      
      if (data.success) {
        toast.success(`${data.summary.corrected_count} Rechnung(en) korrigiert, ${data.summary.emails_sent_count} E-Mail(s) versendet`);
        fetchInvoices();
        fetchStats();
        setAkademieCorrections(null);
      }
    } catch (err) {
      toast.error("Korrektur fehlgeschlagen");
    }
    setAkademieCorrectionLoading(false);
  };

  const downloadInvoice = async (invoiceId, invoiceNumber) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/admin/${invoiceId}/download`, {
        headers: { "X-Admin-Token": adminToken }
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Rechnung_${invoiceNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        toast.error("Download fehlgeschlagen");
      }
    } catch (err) {
      toast.error("Ein Fehler ist aufgetreten");
    }
  };

  const exportCSV = async () => {
    try {
      let url = `${API_URL}/api/invoices/admin/export/csv?`;
      if (dateFrom) url += `date_from=${dateFrom}&`;
      if (dateTo) url += `date_to=${dateTo}&`;

      const res = await fetch(url, {
        headers: { "X-Admin-Token": adminToken }
      });

      if (res.ok) {
        const blob = await res.blob();
        const urlObj = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlObj;
        a.download = `Rechnungen_Export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(urlObj);
        toast.success("CSV exportiert");
      }
    } catch (err) {
      toast.error("Export fehlgeschlagen");
    }
  };

  // Generate Test-PDF without sending email
  const generateTestPdf = async (invoiceId) => {
    setTestPdfLoading(invoiceId);
    setTestPdfResult(null);
    
    try {
      const res = await fetch(`${API_URL}/api/invoices/admin/akademie/generate-test-pdf/${invoiceId}`, {
        method: "POST",
        headers: { "X-Admin-Token": adminToken }
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        setTestPdfResult(data);
        
        // Download the PDF
        const byteCharacters = atob(data.pdf_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.pdf_filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        
        toast.success("Test-PDF generiert und heruntergeladen - KEINE E-Mail versendet!");
      } else {
        toast.error(data.detail || "Fehler beim Generieren der Test-PDF");
      }
    } catch (err) {
      toast.error("Ein Fehler ist aufgetreten");
    } finally {
      setTestPdfLoading(null);
    }
  };

  // Audit offene Forderungen
  const fetchAuditOutstanding = async () => {
    setAuditLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/invoices/admin/audit/outstanding`, {
        headers: { "X-Admin-Token": adminToken }
      });
      const data = await res.json();
      if (res.ok) {
        setAuditData(data);
        setShowAuditDialog(true);
      } else {
        toast.error(data.detail || "Fehler beim Laden des Audits");
      }
    } catch (err) {
      toast.error("Fehler beim Laden des Audits");
    } finally {
      setAuditLoading(false);
    }
  };

  const exportExcel = async () => {
    try {
      let url = `${API_URL}/api/invoices/admin/export/excel?`;
      if (dateFrom) url += `date_from=${dateFrom}&`;
      if (dateTo) url += `date_to=${dateTo}&`;

      const res = await fetch(url, {
        headers: { "X-Admin-Token": adminToken }
      });

      if (res.ok) {
        const blob = await res.blob();
        const urlObj = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlObj;
        a.download = `Rechnungen_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(urlObj);
        toast.success("Excel exportiert");
      }
    } catch (err) {
      toast.error("Export fehlgeschlagen");
    }
  };

  // DATEV Export
  const exportDATEV = async () => {
    try {
      let url = `${API_URL}/api/invoices/admin/export/datev?`;
      if (dateFrom) url += `date_from=${dateFrom}&`;
      if (dateTo) url += `date_to=${dateTo}&`;

      const res = await fetch(url, {
        headers: { "X-Admin-Token": adminToken }
      });

      if (res.ok) {
        const blob = await res.blob();
        const urlObj = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlObj;
        a.download = `DATEV_Export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(urlObj);
        toast.success("DATEV-Export erstellt");
      }
    } catch (err) {
      toast.error("DATEV-Export fehlgeschlagen");
    }
  };

  // Bulk PDF ZIP Download
  const exportBulkPDF = async () => {
    try {
      let url = `${API_URL}/api/invoices/admin/export/pdf-zip?`;
      if (dateFrom) url += `date_from=${dateFrom}&`;
      if (dateTo) url += `date_to=${dateTo}&`;

      toast.info("PDF-Paket wird erstellt...");

      const res = await fetch(url, {
        headers: { "X-Admin-Token": adminToken }
      });

      if (res.ok) {
        const blob = await res.blob();
        const urlObj = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlObj;
        a.download = `Rechnungen_PDF_${new Date().toISOString().split('T')[0]}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(urlObj);
        toast.success("PDF-Paket heruntergeladen");
      } else {
        const data = await res.json();
        toast.error(data.detail || "Download fehlgeschlagen");
      }
    } catch (err) {
      toast.error("Download fehlgeschlagen");
    }
  };

  // Payment Reminder
  const sendPaymentReminder = async (invoiceId) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/admin/${invoiceId}/send-reminder`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Token": adminToken
        },
        body: JSON.stringify({ custom_message: reminderMessage || null })
      });

      const data = await res.json();
      if (data.success) {
        toast.success("Zahlungserinnerung gesendet");
        setShowReminderModal(false);
        setReminderMessage("");
      } else {
        toast.error(data.detail || "Fehler beim Senden");
      }
    } catch (err) {
      toast.error("Ein Fehler ist aufgetreten");
    }
  };

  // Recurring Invoices
  const fetchRecurringInvoices = async () => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/admin/recurring/all`, {
        headers: { "X-Admin-Token": adminToken }
      });
      const data = await res.json();
      setRecurringInvoices(data.recurring_invoices || []);
    } catch (err) {
      console.error("Failed to fetch recurring invoices:", err);
    }
  };

  // Price Corrections
  const fetchPriceCorrections = async () => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/admin/price-corrections/preview`, {
        headers: { "X-Admin-Token": adminToken }
      });
      const data = await res.json();
      setPriceCorrections(data);
    } catch (err) {
      console.error("Failed to fetch price corrections:", err);
    }
  };

  const executePriceCorrections = async () => {
    setCorrectionInProgress(true);
    try {
      const res = await fetch(`${API_URL}/api/invoices/admin/price-corrections/execute`, {
        method: "POST",
        headers: { "X-Admin-Token": adminToken }
      });
      const data = await res.json();
      setCorrectionResult(data);
      
      if (data.success) {
        toast.success(`${data.corrected_count} Rechnung(en) korrigiert, ${data.sent_count} E-Mail(s) versendet`);
        fetchInvoices();
        fetchStats();
        fetchPriceCorrections();
      } else {
        toast.error("Fehler bei der Korrektur");
      }
    } catch (err) {
      toast.error("Ein Fehler ist aufgetreten");
    } finally {
      setCorrectionInProgress(false);
    }
  };

  const createRecurringInvoice = async () => {
    if (!newRecurring.customer_name || !newRecurring.customer_email || !newRecurring.amount || !newRecurring.next_invoice_date) {
      toast.error("Bitte alle Pflichtfelder ausfüllen");
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/invoices/admin/recurring/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Token": adminToken
        },
        body: JSON.stringify({
          customer_name: newRecurring.customer_name,
          customer_email: newRecurring.customer_email,
          customer_phone: newRecurring.customer_phone || null,
          service_category: newRecurring.service_category,
          line_items: [{
            description: newRecurring.description || "Wiederkehrende Leistung",
            quantity: 1,
            unit_price: newRecurring.amount,
            total_price: newRecurring.amount
          }],
          payment_method: newRecurring.payment_method,
          interval_months: newRecurring.interval_months,
          next_invoice_date: newRecurring.next_invoice_date,
          is_active: true,
          notes: newRecurring.notes || null
        })
      });

      if (res.ok) {
        toast.success("Wiederkehrende Rechnung erstellt");
        fetchRecurringInvoices();
        setShowRecurringModal(false);
        setNewRecurring({
          customer_name: "",
          customer_email: "",
          customer_phone: "",
          service_category: "personal_training",
          description: "",
          amount: "",
          payment_method: "bank_transfer",
          interval_months: 1,
          next_invoice_date: "",
          notes: ""
        });
      } else {
        toast.error("Fehler beim Erstellen");
      }
    } catch (err) {
      toast.error("Ein Fehler ist aufgetreten");
    }
  };

  const toggleRecurringActive = async (recurringId, isActive) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/admin/recurring/${recurringId}?is_active=${!isActive}`, {
        method: "PATCH",
        headers: { "X-Admin-Token": adminToken }
      });

      if (res.ok) {
        toast.success(isActive ? "Deaktiviert" : "Aktiviert");
        fetchRecurringInvoices();
      }
    } catch (err) {
      toast.error("Ein Fehler ist aufgetreten");
    }
  };

  const generateRecurringInvoice = async (recurringId) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/admin/recurring/${recurringId}/generate`, {
        method: "POST",
        headers: { "X-Admin-Token": adminToken }
      });

      if (res.ok) {
        toast.success("Rechnung erstellt und versendet");
        fetchRecurringInvoices();
        fetchInvoices();
        fetchStats();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Fehler beim Erstellen");
      }
    } catch (err) {
      toast.error("Ein Fehler ist aufgetreten");
    }
  };

  const deleteRecurringInvoice = async (recurringId) => {
    if (!confirm("Wiederkehrende Rechnung wirklich löschen?")) return;

    try {
      const res = await fetch(`${API_URL}/api/invoices/admin/recurring/${recurringId}`, {
        method: "DELETE",
        headers: { "X-Admin-Token": adminToken }
      });

      if (res.ok) {
        toast.success("Gelöscht");
        fetchRecurringInvoices();
      }
    } catch (err) {
      toast.error("Ein Fehler ist aufgetreten");
    }
  };

  // Filter invoices locally for search
  const filteredInvoices = invoices.filter(inv => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      inv.invoice_number?.toLowerCase().includes(term) ||
      inv.customer_name?.toLowerCase().includes(term) ||
      inv.customer_email?.toLowerCase().includes(term)
    );
  });

  return (
    <div className="space-y-6" data-testid="buchhaltung-tab">
      {/* Sub-Tab Navigation */}
      <div className="flex gap-2 border-b border-white/10 pb-4">
        <Button
          variant={activeSubTab === "invoices" ? "default" : "ghost"}
          onClick={() => setActiveSubTab("invoices")}
          className={activeSubTab === "invoices" ? "bg-[#c1ff72] text-[#0A0A0A]" : ""}
        >
          <Receipt className="w-4 h-4 mr-2" />
          Rechnungen
        </Button>
        <Button
          variant={activeSubTab === "recurring" ? "default" : "ghost"}
          onClick={() => setActiveSubTab("recurring")}
          className={activeSubTab === "recurring" ? "bg-[#c1ff72] text-[#0A0A0A]" : ""}
        >
          <Repeat className="w-4 h-4 mr-2" />
          Wiederkehrende
          {recurringInvoices.length > 0 && (
            <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-xs">
              {recurringInvoices.length}
            </span>
          )}
        </Button>
      </div>

      {/* Stats Cards - Always visible */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-[#1A1A1A] p-4 rounded-lg border border-white/10">
            <div className="flex items-center gap-2 text-[#71717A] text-sm mb-1">
              <Euro className="w-4 h-4" />
              Heute
            </div>
            <p className="text-2xl font-bold text-[#c1ff72]">{stats.revenue?.today?.toFixed(2) || "0.00"} EUR</p>
          </div>
          <div className="bg-[#1A1A1A] p-4 rounded-lg border border-white/10">
            <div className="flex items-center gap-2 text-[#71717A] text-sm mb-1">
              <TrendingUp className="w-4 h-4" />
              Dieser Monat
            </div>
            <p className="text-2xl font-bold text-[#c1ff72]">{stats.revenue?.this_month?.toFixed(2) || "0.00"} EUR</p>
          </div>
          <div className="bg-[#1A1A1A] p-4 rounded-lg border border-white/10">
            <div className="flex items-center gap-2 text-[#71717A] text-sm mb-1">
              <Calendar className="w-4 h-4" />
              Dieses Jahr
            </div>
            <p className="text-2xl font-bold text-white">{stats.revenue?.this_year?.toFixed(2) || "0.00"} EUR</p>
          </div>
          <div className="bg-[#1A1A1A] p-4 rounded-lg border border-yellow-500/30">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2 text-[#71717A] text-sm">
                <Clock className="w-4 h-4" />
                Offene Forderungen
              </div>
              <Button
                onClick={fetchAuditOutstanding}
                variant="ghost"
                size="sm"
                disabled={auditLoading}
                className="text-xs text-[#71717A] hover:text-white h-6 px-2"
              >
                {auditLoading ? (
                  <RefreshCw className="w-3 h-3 animate-spin" />
                ) : (
                  <>
                    <Search className="w-3 h-3 mr-1" />
                    Audit
                  </>
                )}
              </Button>
            </div>
            <p className="text-2xl font-bold text-yellow-500">{stats.revenue?.outstanding?.toFixed(2) || "0.00"} EUR</p>
          </div>
        </div>
      )}
      
      {/* Audit Dialog */}
      <Dialog open={showAuditDialog} onOpenChange={setShowAuditDialog}>
        <DialogContent className="bg-[#0A0A0A] border-[#333] max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-yellow-500" />
              Audit: Offene Forderungen
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Detaillierte Auflistung aller Rechnungen in &quot;Offene Forderungen&quot;
            </DialogDescription>
          </DialogHeader>
          
          {auditData && (
            <div className="space-y-4">
              {/* Zusammenfassung */}
              <div className={`p-4 rounded-lg border ${
                auditData.summary?.difference !== 0 
                  ? "bg-red-500/10 border-red-500/30" 
                  : "bg-green-500/10 border-green-500/30"
              }`}>
                <h3 className="font-semibold text-white mb-3">Zusammenfassung</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-[#71717A]">Aktuell angezeigt:</span>
                    <p className="text-white font-bold text-lg">{auditData.summary?.currently_displayed?.toFixed(2)} EUR</p>
                  </div>
                  <div>
                    <span className="text-[#71717A]">Tatsächlich korrekt:</span>
                    <p className="text-[#c1ff72] font-bold text-lg">{auditData.summary?.correct_total?.toFixed(2)} EUR</p>
                  </div>
                  <div>
                    <span className="text-[#71717A]">Differenz:</span>
                    <p className={`font-bold text-lg ${auditData.summary?.difference !== 0 ? "text-red-400" : "text-green-400"}`}>
                      {auditData.summary?.difference?.toFixed(2)} EUR
                    </p>
                  </div>
                </div>
                {auditData.summary?.difference !== 0 && (
                  <p className="text-red-400 text-sm mt-2">
                    ⚠️ {auditData.summary?.difference_explanation}
                  </p>
                )}
              </div>
              
              {/* Problematische Rechnungen */}
              {auditData.problematic_invoices?.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <h3 className="font-semibold text-red-400 mb-3 flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    Problematische Rechnungen ({auditData.problematic_invoices.length})
                  </h3>
                  <div className="space-y-2">
                    {auditData.problematic_invoices.map((inv, i) => (
                      <div key={i} className="flex justify-between items-center bg-black/30 rounded p-2 text-sm">
                        <span className="text-white font-mono">{inv.invoice_number}</span>
                        <span className="text-red-400">{inv.amount?.toFixed(2)} EUR</span>
                        <span className="text-[#71717A] text-xs">{inv.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Alle Rechnungen in Offene Forderungen */}
              <div className="bg-[#1A1A1A] border border-[#333] rounded-lg p-4">
                <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-yellow-500" />
                  Rechnungen in &quot;Offene Forderungen&quot; ({auditData.invoices_in_outstanding?.length || 0})
                </h3>
                <div className="overflow-x-auto max-h-[400px]">
                  <table className="w-full text-sm">
                    <thead className="text-[#71717A] border-b border-[#333] sticky top-0 bg-[#1A1A1A]">
                      <tr>
                        <th className="text-left p-2">Rechnung</th>
                        <th className="text-left p-2">Empfänger</th>
                        <th className="text-left p-2">Teilnehmer</th>
                        <th className="text-right p-2">Betrag</th>
                        <th className="text-left p-2">Fälligkeit</th>
                        <th className="text-left p-2">Status</th>
                        <th className="text-left p-2">Hinweis</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditData.invoices_in_outstanding?.map((inv, i) => (
                        <tr key={i} className={`border-b border-[#222] ${inv.warning ? "bg-yellow-500/5" : ""} ${!inv.included_in_outstanding ? "bg-red-500/10" : ""}`}>
                          <td className="p-2 font-mono text-white">{inv.invoice_number}</td>
                          <td className="p-2 text-white">{inv.customer_name}</td>
                          <td className="p-2 text-[#A1A1AA]">{inv.participant_name || "—"}</td>
                          <td className="p-2 text-right font-medium text-[#c1ff72]">{inv.amount} EUR</td>
                          <td className="p-2 text-[#A1A1AA]">{inv.due_date}</td>
                          <td className="p-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              inv.included_in_outstanding ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"
                            }`}>
                              {inv.included_in_outstanding ? "Eingerechnet" : "Ausgeschlossen"}
                            </span>
                          </td>
                          <td className="p-2">
                            {inv.warning && (
                              <span className="text-yellow-400 text-xs">{inv.warning}</span>
                            )}
                            {inv.exclude_reason && (
                              <span className="text-red-400 text-xs">{inv.exclude_reason}</span>
                            )}
                            {inv.replaces && (
                              <span className="text-blue-400 text-xs">Korrektur zu: {inv.replaces}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Nicht zahlbare Rechnungen (Kontrolle) */}
              {auditData.not_payable_invoices?.length > 0 && (
                <div className="bg-[#1A1A1A] border border-[#333] rounded-lg p-4">
                  <h3 className="font-semibold text-[#71717A] mb-3 flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    Kontrolle: &quot;Nicht zahlbar&quot; ({auditData.not_payable_invoices.length}) - ✅ NICHT in Offene Forderungen
                  </h3>
                  <div className="space-y-1 text-sm text-[#71717A]">
                    {auditData.not_payable_invoices.map((inv, i) => (
                      <div key={i} className="flex gap-4">
                        <span className="font-mono">{inv.invoice_number}</span>
                        <span>{inv.amount} EUR</span>
                        <span className="text-green-400">✓ Korrekt ausgeschlossen</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Empfehlung */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-sm text-blue-300">
                ℹ️ {auditData.recommendation}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAuditDialog(false)}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {activeSubTab === "invoices" && (
        <>
      {/* Akademie Massen-Korrektur Button */}
      <div className="bg-[#1A1A1A] border border-[#c1ff72]/30 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-[#c1ff72] mb-1">Akademie-Rechnungen korrigieren</h3>
            <p className="text-sm text-[#A1A1AA]">
              Alle Jahresvertrag-Rechnungen auf korrekten Preis (64,83 EUR) und Fälligkeit (01.09.2026) prüfen und korrigieren.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={fetchAkademieCorrections}
              variant="outline"
              className="border-[#c1ff72]/50"
            >
              <Search className="w-4 h-4 mr-2" />
              Prüfen
            </Button>
          </div>
        </div>
        
        {/* Akademie Corrections Preview - VOLLSTÄNDIG ÜBERARBEITET */}
        {akademieCorrections && (
          akademieCorrections.summary?.needs_correction > 0 || 
          akademieCorrections.summary?.manual_review_required > 0 ||
          akademieCorrections.summary?.already_corrected > 0
        ) && !akademieCorrectionResult && (
          <div className="mt-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
            {/* Header mit Zusammenfassung */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-yellow-500 font-semibold text-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Massenkorrektur-Vorschau
                </p>
                <p className="text-xs text-[#71717A] mt-1">
                  {akademieCorrections.hinweis}
                </p>
              </div>
              <div className="text-right text-xs space-y-1">
                <div className="text-[#71717A]">Geprüft: {akademieCorrections.summary?.total_checked}</div>
                <div className="text-green-400">Bereits korrekt: {akademieCorrections.summary?.already_correct}</div>
                {akademieCorrections.summary?.already_corrected > 0 && (
                  <div className="text-blue-400">Bereits korrigiert: {akademieCorrections.summary?.already_corrected}</div>
                )}
                <div className="text-yellow-400">Korrektur nötig: {akademieCorrections.summary?.needs_correction}</div>
                {akademieCorrections.summary?.manual_review_required > 0 && (
                  <div className="text-red-400 font-semibold">Manuelle Prüfung: {akademieCorrections.summary?.manual_review_required}</div>
                )}
              </div>
            </div>
            
            {/* ========== BEREITS KORRIGIERT (Info) ========== */}
            {akademieCorrections.invoices_already_corrected?.length > 0 && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-4">
                <p className="text-blue-400 font-medium text-sm flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4" />
                  {akademieCorrections.invoices_already_corrected.length} Rechnung(en) bereits korrigiert
                </p>
                <p className="text-xs text-[#A1A1AA] mb-2">
                  Für diese Rechnungen existiert bereits eine Korrekturrechnung. Sie werden nicht erneut verarbeitet.
                </p>
                <div className="space-y-1 max-h-[150px] overflow-y-auto">
                  {akademieCorrections.invoices_already_corrected.map((inv, index) => (
                    <div key={inv.invoice_id} className="text-xs text-blue-300 flex items-center gap-2">
                      <span className="text-[#71717A]">{inv.invoice_number}</span>
                      <span>→</span>
                      <span>{inv.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* WICHTIG: Referenz-Hinweis */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-4">
              <p className="text-blue-400 font-medium text-sm mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Wichtiger Hinweis
              </p>
              <p className="text-xs text-[#A1A1AA]">
                {akademieCorrections.reference_note}
              </p>
            </div>
            
            {/* Verfügbare Tarife */}
            <div className="bg-[#1A1A1A] border border-[#333] rounded-lg p-3 mb-4">
              <p className="text-[#c1ff72] font-medium text-sm mb-2">Verfügbare Tarife:</p>
              <div className="grid grid-cols-3 gap-3 text-xs">
                {akademieCorrections.verfuegbare_tarife && Object.entries(akademieCorrections.verfuegbare_tarife).map(([key, value]) => (
                  <div key={key} className="bg-black/30 rounded p-2">
                    <span className="text-[#c1ff72] font-medium capitalize">{key}:</span>
                    <p className="text-white mt-1">{value}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* ========== MANUELLE PRÜFUNG ERFORDERLICH ========== */}
            {akademieCorrections.invoices_manual_review?.length > 0 && (
              <div className="mb-6">
                <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-3">
                  <p className="text-red-400 font-bold text-lg flex items-center gap-2 mb-2">
                    <XCircle className="w-5 h-5" />
                    {akademieCorrections.invoices_manual_review.length} Rechnung(en) - MANUELLE PRÜFUNG ERFORDERLICH
                  </p>
                  <p className="text-red-300 text-sm">
                    Diese Rechnungen können NICHT automatisch korrigiert werden. Bitte manuell prüfen und ggf. Bewerbungsdaten vervollständigen.
                  </p>
                </div>
                
                <div className="space-y-4 max-h-[400px] overflow-y-auto">
                  {akademieCorrections.invoices_manual_review.map((inv, index) => (
                    <div key={inv.invoice_id} className="rounded-lg p-4 border bg-red-500/10 border-red-500/50">
                      <div className="flex justify-between items-start mb-3 pb-2 border-b border-red-500/30">
                        <div className="flex items-center gap-3">
                          <span className="bg-red-500/30 text-red-400 px-2 py-1 rounded font-mono text-sm">
                            #{index + 1}
                          </span>
                          <span className="font-bold text-white text-lg">{inv.invoice_number}</span>
                        </div>
                        <div className="bg-red-500 text-white px-3 py-1 rounded-lg text-sm font-bold">
                          MANUELLE PRÜFUNG
                        </div>
                      </div>
                      
                      {/* Gründe für manuelle Prüfung */}
                      <div className="bg-red-500/20 rounded-lg p-3 mb-3">
                        <p className="text-red-400 font-medium text-sm mb-2">Warum manuelle Prüfung?</p>
                        {inv.manual_review_reasons?.map((reason, i) => (
                          <p key={i} className="text-red-300 text-xs flex items-start gap-2">
                            <span>•</span> {reason}
                          </p>
                        ))}
                      </div>
                      
                      {/* Verfügbare Daten anzeigen */}
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-[#71717A] text-xs mb-1">Aktuell auf Rechnung:</p>
                          <p className="text-white">{inv.aktuell_auf_rechnung?.rechnungsempfaenger || "—"}</p>
                          <p className="text-[#71717A] text-xs">{inv.aktuell_auf_rechnung?.betrag} EUR</p>
                        </div>
                        <div>
                          <p className="text-[#71717A] text-xs mb-1">Bewerbungs-ID:</p>
                          <p className="text-white">{inv.bewerbung_id || "Keine"}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* ========== RECHNUNGEN ZUR KORREKTUR ========== */}
            {akademieCorrections.invoices_to_correct?.length > 0 && (
              <div>
                <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-3 mb-3">
                  <p className="text-yellow-400 font-bold flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    {akademieCorrections.invoices_to_correct.length} Rechnung(en) bereit zur Korrektur
                  </p>
                </div>
                
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {akademieCorrections.invoices_to_correct.map((inv, index) => (
                    <div key={inv.invoice_id} className="rounded-lg p-4 border bg-black/40 border-[#c1ff72]/20">
                      {/* Header */}
                      <div className="flex justify-between items-start mb-3 pb-2 border-b border-white/10">
                        <div className="flex items-center gap-3">
                          <span className="bg-[#c1ff72]/20 text-[#c1ff72] px-2 py-1 rounded font-mono text-sm">
                            #{index + 1}
                          </span>
                          <span className="font-bold text-white text-lg">{inv.invoice_number}</span>
                        </div>
                        <div className="bg-[#c1ff72]/20 text-[#c1ff72] px-3 py-1 rounded-lg text-sm font-medium">
                          Bereit zur Korrektur
                        </div>
                      </div>
                      
                      {/* VERTRAG aus Bewerbung - SINGLE SOURCE OF TRUTH */}
                      <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 mb-3">
                        <p className="text-purple-400 font-semibold text-sm mb-2 flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          VERTRAG (aus ursprünglicher Bewerbung)
                        </p>
                        <div className="grid grid-cols-3 gap-3 text-sm">
                          <div>
                            <span className="text-[#71717A] text-xs">Gewählter Tarif:</span>
                            <p className="text-white font-medium">{inv.vertrag?.tarif_name || "—"}</p>
                          </div>
                          <div>
                            <span className="text-[#71717A] text-xs">Korrekter Monatspreis:</span>
                            <p className="text-[#c1ff72] font-bold">{inv.vertrag?.korrekter_preis} EUR</p>
                          </div>
                          <div>
                            <span className="text-[#71717A] text-xs">Laufzeit:</span>
                            <p className="text-white">{inv.vertrag?.laufzeit || "—"}</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Zwei-Spalten: BISHER vs. KORRIGIERT */}
                      <div className="grid grid-cols-2 gap-4">
                        {/* LINKE SPALTE: Bisherige Daten */}
                        <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3">
                          <p className="text-red-400 font-semibold text-sm mb-3 flex items-center gap-1">
                            <XCircle className="w-4 h-4" />
                            BISHER (auf Rechnung)
                          </p>
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="text-[#71717A] text-xs">Rechnungsempfänger:</span>
                              <p className="text-white">{inv.aktuell_auf_rechnung?.rechnungsempfaenger || "—"}</p>
                            </div>
                            <div>
                              <span className="text-[#71717A] text-xs">Adresse:</span>
                              <p className="text-white">{inv.aktuell_auf_rechnung?.adresse || <span className="text-red-400">Nicht gesetzt</span>}</p>
                            </div>
                            <div>
                              <span className="text-[#71717A] text-xs">E-Mail:</span>
                              <p className="text-white">{inv.aktuell_auf_rechnung?.email || "—"}</p>
                            </div>
                            <div>
                              <span className="text-[#71717A] text-xs">Teilnehmer:</span>
                              <p className="text-white">{inv.aktuell_auf_rechnung?.teilnehmer || <span className="text-red-400">Nicht gesetzt</span>}</p>
                            </div>
                            <div className="border-t border-white/10 pt-2 mt-2">
                              <span className="text-[#71717A] text-xs">Betrag:</span>
                              <p className="text-red-400 font-medium">{inv.aktuell_auf_rechnung?.betrag} EUR</p>
                            </div>
                            <div>
                              <span className="text-[#71717A] text-xs">Fälligkeit:</span>
                              <p className="text-red-400">{inv.aktuell_auf_rechnung?.faelligkeit || "Nicht gesetzt"}</p>
                            </div>
                            <div>
                              <span className="text-[#71717A] text-xs">Leistungszeitraum:</span>
                              <p className="text-red-400">{inv.aktuell_auf_rechnung?.leistungszeitraum || "Nicht gesetzt"}</p>
                            </div>
                          </div>
                        </div>
                        
                        {/* RECHTE SPALTE: Korrigierte Daten */}
                        <div className="bg-green-500/5 border border-green-500/20 rounded-lg p-3">
                          <p className="text-green-400 font-semibold text-sm mb-3 flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" />
                            KORRIGIERT (aus Bewerbung)
                          </p>
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="text-[#71717A] text-xs">Rechnungsempfänger (Elternteil):</span>
                              <p className="text-white font-medium">
                                {inv.rechnungsempfaenger?.name || "—"}
                                {inv.rechnungsempfaenger?.verhaeltnis && (
                                  <span className="ml-2 text-[#71717A] text-xs">({inv.rechnungsempfaenger.verhaeltnis})</span>
                                )}
                              </p>
                            </div>
                            <div>
                              <span className="text-[#71717A] text-xs">Adresse:</span>
                              <p className="text-white">{inv.rechnungsempfaenger?.adresse?.vollstaendig || "—"}</p>
                            </div>
                            <div>
                              <span className="text-[#71717A] text-xs">E-Mail:</span>
                              <p className="text-white">{inv.rechnungsempfaenger?.email || "—"}</p>
                            </div>
                            <div>
                              <span className="text-[#71717A] text-xs">Teilnehmer (Kind):</span>
                              <p className="text-white">
                                {inv.teilnehmer?.name || "—"}
                                {inv.teilnehmer?.jahrgang && (
                                  <span className="ml-2 text-[#c1ff72] text-xs">{inv.teilnehmer.jahrgang}</span>
                                )}
                              </p>
                            </div>
                            <div className="border-t border-white/10 pt-2 mt-2">
                              <span className="text-[#71717A] text-xs">Neuer Betrag (laut {inv.vertrag?.tarif_name}):</span>
                              <p className="text-green-400 font-bold">{inv.korrigiert?.betrag}</p>
                            </div>
                            <div>
                              <span className="text-[#71717A] text-xs">Fälligkeit:</span>
                              <p className="text-green-400">{inv.korrigiert?.faelligkeit}</p>
                            </div>
                            <div>
                              <span className="text-[#71717A] text-xs">Leistungszeitraum:</span>
                              <p className="text-green-400">{inv.korrigiert?.leistungszeitraum}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Erkannte Probleme */}
                      {inv.issues?.length > 0 && (
                        <div className="mt-3 bg-yellow-500/10 rounded-lg p-3">
                          <p className="text-yellow-400 font-medium text-xs mb-2">Erkannte Probleme ({inv.issue_count}):</p>
                          <div className="space-y-1">
                            {inv.issues.map((issue, i) => (
                              <div key={i} className="flex items-start gap-2 text-xs">
                                <span className="text-yellow-500">•</span>
                                <span className="text-yellow-400">{issue.message}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* IDs und Test-PDF Button */}
                      <div className="mt-3 pt-3 border-t border-white/10 flex justify-between items-center">
                        <div className="text-xs text-[#71717A]">
                          <span>Bewerbungs-ID: {inv.bewerbung_id || "—"}</span>
                          <span className="ml-4">Rechnungs-ID: {inv.invoice_id}</span>
                        </div>
                        <Button
                          onClick={() => generateTestPdf(inv.invoice_id)}
                          disabled={testPdfLoading === inv.invoice_id}
                          variant="outline"
                          size="sm"
                          className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                        >
                          {testPdfLoading === inv.invoice_id ? (
                            <>
                              <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                              Generiere...
                            </>
                          ) : (
                            <>
                              <FileText className="w-3 h-3 mr-1" />
                              Test-PDF (ohne E-Mail)
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Test-PDF Ergebnis anzeigen */}
            {testPdfResult && (
              <div className="mt-4 bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <p className="text-purple-400 font-bold text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Test-PDF Verifizierung
                  </p>
                  <Button
                    onClick={() => setTestPdfResult(null)}
                    variant="ghost"
                    size="sm"
                    className="text-[#71717A] hover:text-white"
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-3">
                  <p className="text-green-400 text-sm flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    {testPdfResult.message}
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Daten aus Bewerbung */}
                  <div className="bg-black/30 rounded-lg p-3">
                    <p className="text-[#c1ff72] font-medium text-sm mb-2">Daten aus Bewerbung:</p>
                    <div className="space-y-1 text-xs">
                      <div><span className="text-[#71717A]">Rechnungsempfänger:</span> <span className="text-white">{testPdfResult.verification?.daten_aus_bewerbung?.rechnungsempfaenger}</span></div>
                      <div><span className="text-[#71717A]">Verhältnis:</span> <span className="text-white">{testPdfResult.verification?.daten_aus_bewerbung?.verhaeltnis || "—"}</span></div>
                      <div><span className="text-[#71717A]">Adresse:</span> <span className="text-white">{testPdfResult.verification?.daten_aus_bewerbung?.adresse}</span></div>
                      <div><span className="text-[#71717A]">E-Mail:</span> <span className="text-white">{testPdfResult.verification?.daten_aus_bewerbung?.email}</span></div>
                      <div><span className="text-[#71717A]">Teilnehmer:</span> <span className="text-white">{testPdfResult.verification?.daten_aus_bewerbung?.teilnehmer}</span></div>
                      <div><span className="text-[#71717A]">Jahrgang:</span> <span className="text-white">{testPdfResult.verification?.daten_aus_bewerbung?.jahrgang}</span></div>
                      <div><span className="text-[#71717A]">Gewählter Tarif:</span> <span className="text-[#c1ff72] font-medium">{testPdfResult.verification?.daten_aus_bewerbung?.gewaehlter_tarif}</span></div>
                      <div><span className="text-[#71717A]">Korrekter Preis:</span> <span className="text-[#c1ff72] font-medium">{testPdfResult.verification?.daten_aus_bewerbung?.korrekter_preis}</span></div>
                    </div>
                  </div>
                  
                  {/* Company Settings */}
                  <div className="bg-black/30 rounded-lg p-3">
                    <p className="text-[#c1ff72] font-medium text-sm mb-2">Firmeneinstellungen auf PDF:</p>
                    <div className="space-y-1 text-xs">
                      <div><span className="text-[#71717A]">Logo:</span> <span className={testPdfResult.verification?.company_settings?.logo === "vorhanden" ? "text-green-400" : "text-red-400"}>{testPdfResult.verification?.company_settings?.logo}</span></div>
                      <div><span className="text-[#71717A]">IBAN:</span> <span className={testPdfResult.verification?.company_settings?.bankverbindung?.iban && testPdfResult.verification?.company_settings?.bankverbindung?.iban !== "FEHLT" ? "text-green-400" : "text-red-400"}>{testPdfResult.verification?.company_settings?.bankverbindung?.iban || "FEHLT"}</span></div>
                      <div><span className="text-[#71717A]">BIC:</span> <span className="text-white">{testPdfResult.verification?.company_settings?.bankverbindung?.bic || "—"}</span></div>
                      <div><span className="text-[#71717A]">Bank:</span> <span className="text-white">{testPdfResult.verification?.company_settings?.bankverbindung?.bank || "—"}</span></div>
                      <div><span className="text-[#71717A]">Kontoinhaber:</span> <span className="text-white">{testPdfResult.verification?.company_settings?.bankverbindung?.kontoinhaber || "—"}</span></div>
                      <div><span className="text-[#71717A]">PayPal Link:</span> <span className={testPdfResult.verification?.company_settings?.paypal?.link && testPdfResult.verification?.company_settings?.paypal?.link !== "FEHLT" ? "text-green-400" : "text-red-400"}>{testPdfResult.verification?.company_settings?.paypal?.link || "FEHLT"}</span></div>
                      <div><span className="text-[#71717A]">PayPal E-Mail:</span> <span className="text-white">{testPdfResult.verification?.company_settings?.paypal?.email || "—"}</span></div>
                    </div>
                  </div>
                </div>
                
                {/* Kleinunternehmer-Text */}
                <div className="mt-3 bg-black/30 rounded-lg p-3">
                  <p className="text-[#c1ff72] font-medium text-sm mb-1">Kleinunternehmerregelung:</p>
                  <p className="text-white text-xs italic">{testPdfResult.verification?.company_settings?.kleinunternehmer_text}</p>
                </div>
                
                <p className="text-xs text-[#71717A] mt-3">
                  Original-Rechnung: {testPdfResult.verification?.original_invoice} | Bewerbungs-ID: {testPdfResult.verification?.bewerbung_id}
                </p>
              </div>
            )}
            
            {/* Execute Button - nur wenn Korrekturen möglich */}
            {akademieCorrections.invoices_to_correct?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 mb-4">
                  <p className="text-orange-400 text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    <strong>Wichtig:</strong> Bitte alle Daten sorgfältig prüfen. Nach Freigabe werden korrigierte Rechnungen generiert und per E-Mail versendet.
                  </p>
                  {akademieCorrections.summary?.manual_review_required > 0 && (
                    <p className="text-red-400 text-xs mt-2 ml-6">
                      {akademieCorrections.summary.manual_review_required} Rechnung(en) erfordern manuelle Prüfung und werden NICHT automatisch korrigiert.
                    </p>
                  )}
                </div>
                
                <Button
                  onClick={executeAkademieCorrections}
                  disabled={akademieCorrectionLoading}
                  className="bg-[#c1ff72] hover:bg-[#a8e65c] text-black font-semibold"
                >
                  {akademieCorrectionLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Wird korrigiert...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      {akademieCorrections.invoices_to_correct.length} Rechnung(en) korrigieren und E-Mails versenden
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
        
        {/* No corrections needed */}
        {akademieCorrections && akademieCorrections.summary?.needs_correction === 0 && akademieCorrections.summary?.manual_review_required === 0 && (
          <div className="mt-4 bg-green-500/10 rounded-lg p-3 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <div>
              <span className="text-green-500 font-medium">Alle Akademie-Rechnungen sind korrekt!</span>
              <span className="text-[#71717A] text-sm ml-2">({akademieCorrections.summary?.already_correct} geprüft)</span>
            </div>
          </div>
        )}
        
        {/* Correction Result */}
        {akademieCorrectionResult && (
          <div className="mt-4 bg-green-500/10 rounded-lg p-3">
            <p className="text-green-500 font-semibold mb-2">Korrektur abgeschlossen!</p>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="bg-black/20 rounded p-2 text-center">
                <p className="text-[#71717A]">Korrigiert</p>
                <p className="text-xl font-bold text-white">{akademieCorrectionResult.summary?.corrected_count || 0}</p>
              </div>
              <div className="bg-black/20 rounded p-2 text-center">
                <p className="text-[#71717A]">E-Mails</p>
                <p className="text-xl font-bold text-green-400">{akademieCorrectionResult.summary?.emails_sent_count || 0}</p>
              </div>
              <div className="bg-black/20 rounded p-2 text-center">
                <p className="text-[#71717A]">Fehler</p>
                <p className="text-xl font-bold text-red-400">{akademieCorrectionResult.summary?.error_count || 0}</p>
              </div>
            </div>
            {akademieCorrectionResult.details?.emails_sent?.length > 0 && (
              <div className="mt-3 text-sm">
                <p className="text-[#71717A] mb-1">E-Mails versendet an:</p>
                {akademieCorrectionResult.details.emails_sent.map((e, i) => (
                  <p key={i} className="text-white">• {e.customer} ({e.email})</p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Price Correction Alert */}
      {priceCorrections && priceCorrections.affected_count > 0 && !correctionResult && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-yellow-500 mb-1">Preiskorrektur erforderlich</h3>
              <p className="text-sm text-[#A1A1AA] mb-3">
                {priceCorrections.affected_count} Rechnung(en) mit altem Jahresvertrag-Preis ({priceCorrections.old_price} EUR) gefunden.
                Diese müssen auf den korrekten Preis ({priceCorrections.new_price} EUR) korrigiert werden.
              </p>
              
              {/* List affected invoices */}
              <div className="bg-black/20 rounded p-3 mb-3 max-h-40 overflow-y-auto">
                {priceCorrections.affected_invoices.map((inv) => (
                  <div key={inv.invoice_id} className="flex justify-between items-center py-1 border-b border-white/5 last:border-0">
                    <span className="text-sm text-white">{inv.customer_name}</span>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="text-red-400 line-through">{inv.old_price} EUR</span>
                      <span className="text-green-400">{inv.new_price} EUR</span>
                      <span className="text-[#71717A]">{inv.invoice_number}</span>
                    </div>
                  </div>
                ))}
              </div>
              
              <Button 
                onClick={() => setShowCorrectionModal(true)}
                className="bg-yellow-500 hover:bg-yellow-600 text-black"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Korrekturen durchführen und neue Rechnungen versenden
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Correction Result */}
      {correctionResult && (
        <div className={`border rounded-lg p-4 ${correctionResult.error_count > 0 ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
          <div className="flex items-start gap-3">
            <CheckCircle className={`w-6 h-6 flex-shrink-0 mt-0.5 ${correctionResult.error_count > 0 ? 'text-yellow-500' : 'text-green-500'}`} />
            <div>
              <h3 className={`font-semibold mb-2 ${correctionResult.error_count > 0 ? 'text-yellow-500' : 'text-green-500'}`}>
                Preiskorrektur abgeschlossen
              </h3>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="bg-black/20 rounded p-2">
                  <p className="text-[#71717A]">Korrigiert</p>
                  <p className="text-xl font-bold text-white">{correctionResult.corrected_count}</p>
                </div>
                <div className="bg-black/20 rounded p-2">
                  <p className="text-[#71717A]">E-Mails versendet</p>
                  <p className="text-xl font-bold text-green-400">{correctionResult.sent_count}</p>
                </div>
                <div className="bg-black/20 rounded p-2">
                  <p className="text-[#71717A]">Fehler</p>
                  <p className="text-xl font-bold text-red-400">{correctionResult.error_count}</p>
                </div>
              </div>
              {correctionResult.errors && correctionResult.errors.length > 0 && (
                <div className="mt-3 text-sm text-red-400">
                  <p className="font-medium mb-1">Fehler:</p>
                  {correctionResult.errors.map((err, i) => (
                    <p key={i}>• {err.invoice_id}: {err.error}</p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Category Breakdown */}
      {stats?.by_category && Object.keys(stats.by_category).length > 0 && (
        <div className="bg-[#1A1A1A] p-4 rounded-lg border border-white/10">
          <h3 className="font-semibold mb-3">Einnahmen nach Kategorie (dieses Jahr)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(stats.by_category).map(([cat, data]) => {
              const config = CATEGORY_CONFIG[cat] || CATEGORY_CONFIG.other;
              return (
                <div key={cat} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div>
                    <span className={`text-xs px-2 py-1 rounded-full ${config.color}`}>{config.label}</span>
                    <p className="text-xs text-[#71717A] mt-1">{data.count} Rechnungen</p>
                  </div>
                  <p className="font-semibold">{data.total?.toFixed(2)} EUR</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters & Actions */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
          <Input
            placeholder="Suche nach Name, E-Mail oder Rechnungsnummer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-[#1A1A1A] border-white/10"
            data-testid="search-invoices"
          />
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); fetchInvoices(); }}>
            <SelectTrigger className="w-[140px] bg-[#1A1A1A] border-white/10">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              <SelectItem value="pending">Offen</SelectItem>
              <SelectItem value="paid">Bezahlt</SelectItem>
              <SelectItem value="cancelled">Storniert</SelectItem>
            </SelectContent>
          </Select>

          <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); fetchInvoices(); }}>
            <SelectTrigger className="w-[140px] bg-[#1A1A1A] border-white/10">
              <SelectValue placeholder="Kategorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Kategorien</SelectItem>
              <SelectItem value="personal_training">Training</SelectItem>
              <SelectItem value="package">Paket</SelectItem>
              <SelectItem value="camp">Camp</SelectItem>
              <SelectItem value="academy">Akademie</SelectItem>
            </SelectContent>
          </Select>

          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-[140px] bg-[#1A1A1A] border-white/10"
            placeholder="Von"
          />
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-[140px] bg-[#1A1A1A] border-white/10"
            placeholder="Bis"
          />

          <Button variant="outline" onClick={fetchInvoices}>
            <RefreshCw className="w-4 h-4" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={exportCSV}>
                <FileText className="w-4 h-4 mr-2" />
                CSV exportieren
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportExcel}>
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel exportieren
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportDATEV}>
                <Building2 className="w-4 h-4 mr-2" />
                DATEV-Export (Steuerberater)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportBulkPDF}>
                <Archive className="w-4 h-4 mr-2" />
                Alle PDFs als ZIP
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-[#1A1A1A] rounded-lg border border-white/10 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[#71717A]">Lade Rechnungen...</div>
        ) : filteredInvoices.length === 0 ? (
          <div className="p-8 text-center text-[#71717A]">Keine Rechnungen gefunden</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left p-4 font-medium text-[#71717A]">Rechnungsnr.</th>
                  <th className="text-left p-4 font-medium text-[#71717A]">Datum</th>
                  <th className="text-left p-4 font-medium text-[#71717A]">Kunde</th>
                  <th className="text-left p-4 font-medium text-[#71717A]">Leistung</th>
                  <th className="text-left p-4 font-medium text-[#71717A]">Kategorie</th>
                  <th className="text-right p-4 font-medium text-[#71717A]">Betrag</th>
                  <th className="text-left p-4 font-medium text-[#71717A]">Zahlung</th>
                  <th className="text-left p-4 font-medium text-[#71717A]">Status</th>
                  <th className="text-right p-4 font-medium text-[#71717A]">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((inv) => {
                  const statusConfig = PAYMENT_STATUS_CONFIG[inv.payment_status] || PAYMENT_STATUS_CONFIG.pending;
                  const categoryConfig = CATEGORY_CONFIG[inv.service_category] || CATEGORY_CONFIG.other;
                  const StatusIcon = statusConfig.icon;
                  const serviceDesc = inv.line_items?.map(i => i.description).join(", ") || "";

                  return (
                    <tr key={inv.invoice_id} className="border-t border-white/5 hover:bg-white/5">
                      <td className="p-4">
                        <span className="font-mono text-[#c1ff72]">{inv.invoice_number}</span>
                        {inv.is_credit_note && (
                          <span className="ml-2 text-xs bg-purple-500/20 text-purple-500 px-2 py-0.5 rounded">Gutschrift</span>
                        )}
                      </td>
                      <td className="p-4 text-[#71717A]">{inv.invoice_date}</td>
                      <td className="p-4">
                        <div className="font-medium">{inv.customer_name}</div>
                        <div className="text-xs text-[#71717A]">{inv.customer_email}</div>
                      </td>
                      <td className="p-4">
                        <div className="max-w-[200px] truncate" title={serviceDesc}>{serviceDesc}</div>
                      </td>
                      <td className="p-4">
                        <span className={`text-xs px-2 py-1 rounded-full ${categoryConfig.color}`}>
                          {categoryConfig.label}
                        </span>
                      </td>
                      <td className="p-4 text-right font-semibold">
                        <span className={inv.is_credit_note ? "text-red-400" : "text-[#c1ff72]"}>
                          {inv.total_amount} EUR
                        </span>
                      </td>
                      <td className="p-4 text-[#71717A]">
                        {PAYMENT_METHOD_LABELS[inv.payment_method] || inv.payment_method}
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${statusConfig.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { setSelectedInvoice(inv); setShowDetailModal(true); }}>
                              <Eye className="w-4 h-4 mr-2" />
                              Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => downloadInvoice(inv.invoice_id, inv.invoice_number)}>
                              <Download className="w-4 h-4 mr-2" />
                              PDF herunterladen
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => resendInvoice(inv.invoice_id)}>
                              <Send className="w-4 h-4 mr-2" />
                              Erneut senden
                            </DropdownMenuItem>
                            {inv.payment_status === "pending" && inv.status !== "cancelled" && (
                              <>
                                <DropdownMenuItem 
                                  onClick={() => { setSelectedInvoice(inv); setShowReminderModal(true); }}
                                  className="text-yellow-500"
                                >
                                  <Bell className="w-4 h-4 mr-2" />
                                  Zahlungserinnerung senden
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => updatePaymentStatus(inv.invoice_id, "paid")}
                                  className="text-green-500"
                                >
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Als bezahlt markieren
                                </DropdownMenuItem>
                              </>
                            )}
                            {inv.status !== "cancelled" && (
                              <DropdownMenuItem 
                                onClick={() => { setSelectedInvoice(inv); setShowCancelModal(true); }}
                                className="text-red-500"
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Stornieren
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invoice counts */}
      {stats && (
        <div className="flex gap-4 text-sm text-[#71717A]">
          <span>Gesamt: {stats.counts?.total || 0}</span>
          <span>Bezahlt: {stats.counts?.paid || 0}</span>
          <span>Offen: {stats.counts?.pending || 0}</span>
          <span>Storniert: {stats.counts?.cancelled || 0}</span>
        </div>
      )}
        </>
      )}

      {/* Recurring Invoices Tab */}
      {activeSubTab === "recurring" && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Wiederkehrende Rechnungen</h3>
              <p className="text-sm text-[#71717A]">Automatische Rechnungen für regelmäßige Kunden</p>
            </div>
            <Button 
              onClick={() => setShowRecurringModal(true)}
              className="bg-[#c1ff72] text-[#0A0A0A] hover:bg-[#a8e65c]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Neue wiederkehrende Rechnung
            </Button>
          </div>

          {/* Recurring List */}
          {recurringInvoices.length === 0 ? (
            <div className="bg-[#1A1A1A] rounded-lg border border-white/10 p-8 text-center">
              <Repeat className="w-12 h-12 mx-auto text-[#71717A] mb-4" />
              <p className="text-[#71717A]">Keine wiederkehrenden Rechnungen eingerichtet</p>
              <p className="text-sm text-[#71717A] mt-1">
                Erstelle wiederkehrende Rechnungen für regelmäßige Kunden wie Monatsabos
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {recurringInvoices.map((rec) => (
                <div 
                  key={rec.recurring_id} 
                  className={`bg-[#1A1A1A] rounded-lg border p-4 ${rec.is_active ? 'border-white/10' : 'border-white/5 opacity-60'}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-semibold">{rec.customer_name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${rec.is_active ? 'bg-green-500/20 text-green-500' : 'bg-gray-500/20 text-gray-500'}`}>
                          {rec.is_active ? 'Aktiv' : 'Pausiert'}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[#c1ff72]/20 text-[#c1ff72]">
                          {rec.interval_months === 1 ? 'Monatlich' : `Alle ${rec.interval_months} Monate`}
                        </span>
                      </div>
                      <p className="text-sm text-[#71717A]">{rec.customer_email}</p>
                      <div className="mt-2 flex items-center gap-4 text-sm">
                        <span className="text-[#c1ff72] font-semibold">
                          {rec.line_items?.[0]?.total_price || '0.00'} EUR
                        </span>
                        <span className="text-[#71717A]">
                          {rec.line_items?.[0]?.description}
                        </span>
                      </div>
                      <div className="mt-2 text-xs text-[#71717A]">
                        Nächste Rechnung: <span className="text-white">{rec.next_invoice_date}</span>
                        {rec.invoices_generated?.length > 0 && (
                          <span className="ml-4">
                            Bisher erstellt: {rec.invoices_generated.length}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => generateRecurringInvoice(rec.recurring_id)}
                        title="Rechnung jetzt erstellen"
                      >
                        <Play className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleRecurringActive(rec.recurring_id, rec.is_active)}
                        title={rec.is_active ? "Pausieren" : "Aktivieren"}
                      >
                        {rec.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteRecurringInvoice(rec.recurring_id)}
                        className="text-red-500 hover:bg-red-500/10"
                        title="Löschen"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#c1ff72]" />
              Rechnung {selectedInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>

          {selectedInvoice && (
            <div className="space-y-6">
              {/* Status Banner */}
              {selectedInvoice.status === "cancelled" && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-500" />
                  <span className="text-red-500 font-medium">Diese Rechnung wurde storniert</span>
                </div>
              )}

              {/* Customer Info */}
              <div className="bg-white/5 rounded-lg p-4">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <User className="w-4 h-4 text-[#c1ff72]" />
                  Kunde
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-[#71717A]">Name:</span>
                    <p>{selectedInvoice.customer_name}</p>
                  </div>
                  <div>
                    <span className="text-[#71717A]">E-Mail:</span>
                    <p>{selectedInvoice.customer_email}</p>
                  </div>
                  {selectedInvoice.customer_phone && (
                    <div>
                      <span className="text-[#71717A]">Telefon:</span>
                      <p>{selectedInvoice.customer_phone}</p>
                    </div>
                  )}
                  {selectedInvoice.customer_address && (
                    <div className="col-span-2">
                      <span className="text-[#71717A]">Adresse:</span>
                      <p>{selectedInvoice.customer_address}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Invoice Details */}
              <div className="bg-white/5 rounded-lg p-4">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#c1ff72]" />
                  Rechnungsdetails
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-[#71717A]">Rechnungsdatum:</span>
                    <p>{selectedInvoice.invoice_date}</p>
                  </div>
                  <div>
                    <span className="text-[#71717A]">Fällig bis:</span>
                    <p>{selectedInvoice.due_date}</p>
                  </div>
                  {selectedInvoice.service_date && (
                    <div>
                      <span className="text-[#71717A]">Leistungsdatum:</span>
                      <p>{selectedInvoice.service_date}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Line Items */}
              <div className="bg-white/5 rounded-lg p-4">
                <h4 className="font-semibold mb-2">Positionen</h4>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[#71717A]">
                      <th className="text-left pb-2">Beschreibung</th>
                      <th className="text-center pb-2">Menge</th>
                      <th className="text-right pb-2">Einzelpreis</th>
                      <th className="text-right pb-2">Gesamt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInvoice.line_items?.map((item, idx) => (
                      <tr key={idx} className="border-t border-white/5">
                        <td className="py-2">{item.description}</td>
                        <td className="py-2 text-center">{item.quantity}</td>
                        <td className="py-2 text-right">{item.unit_price} EUR</td>
                        <td className="py-2 text-right">{item.total_price} EUR</td>
                      </tr>
                    ))}
                    <tr className="border-t border-white/10 font-semibold">
                      <td colSpan={3} className="py-2 text-right">Gesamtbetrag:</td>
                      <td className="py-2 text-right text-[#c1ff72]">{selectedInvoice.total_amount} EUR</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Payment Info */}
              <div className="bg-white/5 rounded-lg p-4">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-[#c1ff72]" />
                  Zahlung
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-[#71717A]">Zahlungsart:</span>
                    <p>{PAYMENT_METHOD_LABELS[selectedInvoice.payment_method]}</p>
                  </div>
                  <div>
                    <span className="text-[#71717A]">Status:</span>
                    <p className={PAYMENT_STATUS_CONFIG[selectedInvoice.payment_status]?.color.replace('bg-', 'text-').split(' ')[1]}>
                      {PAYMENT_STATUS_CONFIG[selectedInvoice.payment_status]?.label}
                    </p>
                  </div>
                  {selectedInvoice.paid_at && (
                    <div>
                      <span className="text-[#71717A]">Bezahlt am:</span>
                      <p>{selectedInvoice.paid_at}</p>
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                {selectedInvoice.status !== "cancelled" && selectedInvoice.payment_status === "pending" && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <Label className="text-[#71717A] mb-2 block">Zahlungsstatus ändern</Label>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="bg-green-500 hover:bg-green-600"
                        onClick={() => updatePaymentStatus(selectedInvoice.invoice_id, "paid")}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Als bezahlt markieren
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Notes */}
              {selectedInvoice.notes && (
                <div className="bg-white/5 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Hinweise</h4>
                  <p className="text-sm text-[#A1A1AA]">{selectedInvoice.notes}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => downloadInvoice(selectedInvoice.invoice_id, selectedInvoice.invoice_number)}>
                  <Download className="w-4 h-4 mr-2" />
                  PDF herunterladen
                </Button>
                <Button variant="outline" onClick={() => resendInvoice(selectedInvoice.invoice_id)}>
                  <Send className="w-4 h-4 mr-2" />
                  Erneut senden
                </Button>
                {selectedInvoice.status !== "cancelled" && (
                  <Button 
                    variant="outline" 
                    className="text-red-500 border-red-500/30 hover:bg-red-500/10"
                    onClick={() => { setShowCancelModal(true); }}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Stornieren
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="bg-[#1A1A1A] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-red-500">Rechnung stornieren</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-[#A1A1AA]">
              Rechnung <span className="text-white font-mono">{selectedInvoice?.invoice_number}</span> wirklich stornieren?
            </p>

            <div>
              <Label>Stornierungsgrund *</Label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Grund für die Stornierung..."
                className="mt-1 bg-[#0A0A0A] border-white/20"
              />
            </div>

            <div>
              <Label>Erstattungsbetrag (optional)</Label>
              <Input
                type="number"
                step="0.01"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                placeholder={selectedInvoice?.total_amount || "0.00"}
                className="mt-1 bg-[#0A0A0A] border-white/20"
              />
              <p className="text-xs text-[#71717A] mt-1">
                Bei Angabe eines Betrags wird eine Gutschrift erstellt.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelModal(false)}>
              Abbrechen
            </Button>
            <Button 
              className="bg-red-500 hover:bg-red-600"
              onClick={cancelInvoice}
            >
              Stornieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Reminder Modal */}
      <Dialog open={showReminderModal} onOpenChange={setShowReminderModal}>
        <DialogContent className="bg-[#1A1A1A] border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-500">
              <Bell className="w-5 h-5" />
              Zahlungserinnerung senden
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-[#A1A1AA]">
              Zahlungserinnerung für Rechnung <span className="text-white font-mono">{selectedInvoice?.invoice_number}</span> senden?
            </p>
            
            <div className="bg-white/5 rounded-lg p-3">
              <p className="text-sm"><strong>Kunde:</strong> {selectedInvoice?.customer_name}</p>
              <p className="text-sm"><strong>Betrag:</strong> {selectedInvoice?.total_amount} EUR</p>
              <p className="text-sm"><strong>Fällig seit:</strong> {selectedInvoice?.due_date}</p>
            </div>

            <div>
              <Label>Eigene Nachricht (optional)</Label>
              <Textarea
                value={reminderMessage}
                onChange={(e) => setReminderMessage(e.target.value)}
                placeholder="Standard-Erinnerungstext wird verwendet wenn leer..."
                className="mt-1 bg-[#0A0A0A] border-white/20"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReminderModal(false)}>
              Abbrechen
            </Button>
            <Button 
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
              onClick={() => sendPaymentReminder(selectedInvoice?.invoice_id)}
            >
              <Mail className="w-4 h-4 mr-2" />
              Erinnerung senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Recurring Invoice Modal */}
      <Dialog open={showRecurringModal} onOpenChange={setShowRecurringModal}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Repeat className="w-5 h-5 text-[#c1ff72]" />
              Wiederkehrende Rechnung erstellen
            </DialogTitle>
            <DialogDescription className="text-[#71717A]">
              Erstelle automatische Rechnungen für regelmäßige Kunden wie Monatsabos
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Kundenname *</Label>
                <Input
                  value={newRecurring.customer_name}
                  onChange={(e) => setNewRecurring(prev => ({ ...prev, customer_name: e.target.value }))}
                  placeholder="Max Mustermann"
                  className="mt-1 bg-[#0A0A0A] border-white/20"
                />
              </div>
              <div className="col-span-2">
                <Label>E-Mail *</Label>
                <Input
                  type="email"
                  value={newRecurring.customer_email}
                  onChange={(e) => setNewRecurring(prev => ({ ...prev, customer_email: e.target.value }))}
                  placeholder="email@beispiel.de"
                  className="mt-1 bg-[#0A0A0A] border-white/20"
                />
              </div>
              <div className="col-span-2">
                <Label>Leistungsbeschreibung *</Label>
                <Input
                  value={newRecurring.description}
                  onChange={(e) => setNewRecurring(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="z.B. Monatsabo Einzeltraining"
                  className="mt-1 bg-[#0A0A0A] border-white/20"
                />
              </div>
              <div>
                <Label>Betrag (EUR) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newRecurring.amount}
                  onChange={(e) => setNewRecurring(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="140.00"
                  className="mt-1 bg-[#0A0A0A] border-white/20"
                />
              </div>
              <div>
                <Label>Kategorie</Label>
                <Select 
                  value={newRecurring.service_category} 
                  onValueChange={(v) => setNewRecurring(prev => ({ ...prev, service_category: v }))}
                >
                  <SelectTrigger className="mt-1 bg-[#0A0A0A] border-white/20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="personal_training">Personal Training</SelectItem>
                    <SelectItem value="package">Paket</SelectItem>
                    <SelectItem value="academy">Akademie</SelectItem>
                    <SelectItem value="other">Sonstiges</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Zahlungsart</Label>
                <Select 
                  value={newRecurring.payment_method} 
                  onValueChange={(v) => setNewRecurring(prev => ({ ...prev, payment_method: v }))}
                >
                  <SelectTrigger className="mt-1 bg-[#0A0A0A] border-white/20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Banküberweisung</SelectItem>
                    <SelectItem value="paypal">PayPal</SelectItem>
                    <SelectItem value="cash">Barzahlung</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Intervall</Label>
                <Select 
                  value={String(newRecurring.interval_months)} 
                  onValueChange={(v) => setNewRecurring(prev => ({ ...prev, interval_months: parseInt(v) }))}
                >
                  <SelectTrigger className="mt-1 bg-[#0A0A0A] border-white/20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Monatlich</SelectItem>
                    <SelectItem value="3">Vierteljährlich</SelectItem>
                    <SelectItem value="6">Halbjährlich</SelectItem>
                    <SelectItem value="12">Jährlich</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Nächstes Rechnungsdatum *</Label>
                <Input
                  type="date"
                  value={newRecurring.next_invoice_date}
                  onChange={(e) => setNewRecurring(prev => ({ ...prev, next_invoice_date: e.target.value }))}
                  className="mt-1 bg-[#0A0A0A] border-white/20"
                />
              </div>
              <div className="col-span-2">
                <Label>Notizen (optional)</Label>
                <Textarea
                  value={newRecurring.notes}
                  onChange={(e) => setNewRecurring(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Interne Notizen..."
                  className="mt-1 bg-[#0A0A0A] border-white/20"
                  rows={2}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecurringModal(false)}>
              Abbrechen
            </Button>
            <Button 
              className="bg-[#c1ff72] text-[#0A0A0A] hover:bg-[#a8e65c]"
              onClick={createRecurringInvoice}
            >
              Erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Price Correction Confirmation Modal */}
      <Dialog open={showCorrectionModal} onOpenChange={setShowCorrectionModal}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-500">
              <AlertCircle className="w-5 h-5" />
              Preiskorrektur bestaetigen
            </DialogTitle>
            <DialogDescription className="text-[#71717A]">
              Diese Aktion kann nicht rueckgaengig gemacht werden
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-[#A1A1AA]">
              Folgende Aktionen werden durchgefuehrt:
            </p>
            
            <div className="bg-black/30 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-400" />
                <span>{priceCorrections?.affected_count || 0} alte Rechnung(en) werden storniert</span>
              </div>
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-green-400" />
                <span>{priceCorrections?.affected_count || 0} neue Rechnung(en) mit 64,83 EUR werden erstellt</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-blue-400" />
                <span>E-Mails mit korrigierten Rechnungen werden automatisch versendet</span>
              </div>
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-3 text-sm">
              <p className="text-yellow-500 font-medium">Hinweis:</p>
              <p className="text-[#A1A1AA]">
                Die Kunden erhalten eine E-Mail mit Erklaerung zur Preiskorrektur und der neuen Rechnung als PDF-Anhang.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCorrectionModal(false)}>
              Abbrechen
            </Button>
            <Button 
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
              onClick={() => {
                setShowCorrectionModal(false);
                executePriceCorrections();
              }}
              disabled={correctionInProgress}
            >
              {correctionInProgress ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Wird verarbeitet...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Korrekturen durchfuehren und versenden
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
